import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { Label } from '../../../components/ui/label'
import { Coins, Cpu, Settings } from 'lucide-react'
import { API_BASE_URL } from '../../../lib/config'
import { AdminPanelButton } from './AdminPanelButton'

type LlmOption = { id: string; label: string }

type TierDraft = { msg: string; usd: string }

const TIER_IDS = ['explorer', 'builder', 'pioneer'] as const

/** Flex column forms default to stretch; keep primary actions content-sized and left-aligned. */
const configSubmitButtonClassName = 'w-fit self-start shrink-0'

export function ConfigurationsSection() {
  const [textareaValue, setTextareaValue] = useState('')
  const [llmProvider, setLlmProvider] = useState('')
  /** Global model from last load (or last successful save); used to detect radio-only changes. */
  const [baselineLlmProvider, setBaselineLlmProvider] = useState('')
  const [llmOptions, setLlmOptions] = useState<LlmOption[]>([])
  const [maxTokensByProvider, setMaxTokensByProvider] = useState<Record<string, number>>({})
  const [defaultMaxTokensByProvider, setDefaultMaxTokensByProvider] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savingLlm, setSavingLlm] = useState(false)
  const [error, setError] = useState('')
  const [llmError, setLlmError] = useState('')
  const [success, setSuccess] = useState(false)
  const [llmSuccess, setLlmSuccess] = useState(false)
  const [llmSuccessMessage, setLlmSuccessMessage] = useState<string | null>(null)
  const [tierDrafts, setTierDrafts] = useState<Record<string, TierDraft>>(() =>
    Object.fromEntries(TIER_IDS.map((id) => [id, { msg: '', usd: '' }]))
  )
  const [tierLabels, setTierLabels] = useState<Record<string, string>>({})
  const [savingCredit, setSavingCredit] = useState(false)
  const [creditError, setCreditError] = useState('')
  const [creditSuccess, setCreditSuccess] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([
      fetch(`${API_BASE_URL}admin/configurations/login-passcodes`, {
        method: 'GET',
        credentials: 'include',
      }).then((res) => (res.ok ? res.json() : null)),
      fetch(`${API_BASE_URL}admin/configurations/llm-provider`, {
        method: 'GET',
        credentials: 'include',
      }).then((res) => (res.ok ? res.json() : null)),
      fetch(`${API_BASE_URL}admin/configurations/credit-tiers`, {
        method: 'GET',
        credentials: 'include',
      }).then((res) => (res.ok ? res.json() : null)),
    ])
      .then(([passData, llmData, creditData]) => {
        if (cancelled) return
        if (passData && Array.isArray(passData.passcodes)) {
          setTextareaValue(passData.passcodes.filter((p: string) => p != null).join('\n'))
        }
        if (creditData && typeof creditData === 'object' && creditData.tiers) {
          const tiers = creditData.tiers as Record<
            string,
            { monthly_message_limit?: number | null; monthly_cost_usd_limit?: number | null }
          >
          const labels = (creditData as { tier_labels?: Record<string, string> }).tier_labels
          if (labels && typeof labels === 'object') setTierLabels(labels)
          const next: Record<string, TierDraft> = {}
          for (const id of TIER_IDS) {
            const t = tiers[id]
            next[id] = {
              msg:
                t?.monthly_message_limit != null && t.monthly_message_limit !== ''
                  ? String(t.monthly_message_limit)
                  : '',
              usd:
                t?.monthly_cost_usd_limit != null && t.monthly_cost_usd_limit !== ''
                  ? String(t.monthly_cost_usd_limit)
                  : '',
            }
          }
          setTierDrafts(next)
        }
        if (llmData && typeof llmData.provider === 'string') {
          setLlmProvider(llmData.provider)
          setBaselineLlmProvider(llmData.provider)
          if (Array.isArray(llmData.options)) {
            setLlmOptions(llmData.options as LlmOption[])
          }
          if (llmData.max_tokens_by_provider && typeof llmData.max_tokens_by_provider === 'object') {
            const m: Record<string, number> = {}
            for (const [k, v] of Object.entries(
              llmData.max_tokens_by_provider as Record<string, unknown>
            )) {
              const n = Number(v)
              if (!Number.isNaN(n)) m[k] = n
            }
            setMaxTokensByProvider(m)
          }
          if (
            llmData.default_max_tokens_by_provider &&
            typeof llmData.default_max_tokens_by_provider === 'object'
          ) {
            const dm: Record<string, number> = {}
            for (const [k, v] of Object.entries(
              llmData.default_max_tokens_by_provider as Record<string, unknown>
            )) {
              const n = Number(v)
              if (!Number.isNaN(n)) dm[k] = n
            }
            setDefaultMaxTokensByProvider(dm)
          }
        }
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load configurations')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const llmChoices: LlmOption[] =
    llmOptions.length > 0
      ? llmOptions
      : [
          { id: 'claude_sonnet_5', label: 'Claude Sonnet 5 (default)' },
          { id: 'claude_sonnet_4_6', label: 'Claude Sonnet 4.6 (legacy)' },
          { id: 'claude_sonnet_4_5', label: 'Claude Sonnet 4.5 (legacy)' },
          { id: 'gpt_4o', label: 'GPT-4o' },
          { id: 'gpt_5_1', label: 'GPT-5.1' },
        ]

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess(false)
    const list = textareaValue
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean)
    setSaving(true)
    try {
      const res = await fetch(`${API_BASE_URL}admin/configurations/login-passcodes`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ passcodes: list }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(data.error || data.detail || 'Failed to save')
        return
      }
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch {
      setError('Network error')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveLlm = async (e: React.FormEvent) => {
    e.preventDefault()
    setLlmError('')
    setLlmSuccess(false)
    setLlmSuccessMessage(null)
    setSavingLlm(true)
    try {
      const mergedMax: Record<string, number> = {}
      for (const opt of llmChoices) {
        const v =
          maxTokensByProvider[opt.id] ??
          defaultMaxTokensByProvider[opt.id] ??
          4096
        mergedMax[opt.id] = Math.max(1, Math.min(262144, v))
      }
      const providerSelectionChanged = llmProvider !== baselineLlmProvider
      const res = await fetch(`${API_BASE_URL}admin/configurations/llm-provider`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          provider: llmProvider,
          max_tokens_by_provider: mergedMax,
          provider_selection_changed: providerSelectionChanged,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setLlmError(data.error || data.detail || 'Failed to save')
        return
      }
      if (typeof data.provider === 'string') {
        setLlmProvider(data.provider)
        setBaselineLlmProvider(data.provider)
      }
      if (data.max_tokens_by_provider && typeof data.max_tokens_by_provider === 'object') {
        const m: Record<string, number> = {}
        for (const [k, v] of Object.entries(
          data.max_tokens_by_provider as Record<string, unknown>
        )) {
          const n = Number(v)
          if (!Number.isNaN(n)) m[k] = n
        }
        setMaxTokensByProvider(m)
      }
      const cleared = (data as { cleared_user_chat_model_overrides?: number })
        .cleared_user_chat_model_overrides
      if (typeof cleared === 'number' && cleared > 0) {
        setLlmError('')
        // Success copy: global model changed, per-user overrides removed so all users get the new default
        setLlmSuccessMessage(
          `Saved. Default model updated for all users (${cleared} per-user model override${cleared === 1 ? '' : 's'} cleared).`
        )
        setLlmSuccess(true)
        setTimeout(() => {
          setLlmSuccess(false)
          setLlmSuccessMessage(null)
        }, 5000)
      } else {
        setLlmSuccessMessage(null)
        setLlmSuccess(true)
        setTimeout(() => setLlmSuccess(false), 3000)
      }
    } catch {
      setLlmError('Network error')
    } finally {
      setSavingLlm(false)
    }
  }

  const handleSaveCredit = async (e: React.FormEvent) => {
    e.preventDefault()
    setCreditError('')
    setCreditSuccess(false)
    setSavingCredit(true)
    try {
      const tiers: Record<
        string,
        { monthly_message_limit: number | null; monthly_cost_usd_limit: number | null }
      > = {}
      for (const id of TIER_IDS) {
        const d = tierDrafts[id]
        const mt = (d?.msg ?? '').trim()
        const ct = (d?.usd ?? '').trim()
        let ml: number | null = null
        let cl: number | null = null
        if (mt !== '') {
          const n = parseInt(mt, 10)
          if (Number.isNaN(n) || n < 1) {
            setCreditError(`${tierLabels[id] ?? id}: message cap must be empty or a positive whole number.`)
            return
          }
          ml = n
        }
        if (ct !== '') {
          const x = parseFloat(ct)
          if (Number.isNaN(x) || x <= 0) {
            setCreditError(`${tierLabels[id] ?? id}: USD cap must be empty or a positive amount.`)
            return
          }
          cl = x
        }
        tiers[id] = { monthly_message_limit: ml, monthly_cost_usd_limit: cl }
      }
      const res = await fetch(`${API_BASE_URL}admin/configurations/credit-tiers`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tiers }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setCreditError(data.error || data.detail || 'Failed to save tier limits')
        return
      }
      if (data.tiers && typeof data.tiers === 'object') {
        const merged = data.tiers as Record<
          string,
          { monthly_message_limit?: number | null; monthly_cost_usd_limit?: number | null }
        >
        const next: Record<string, TierDraft> = {}
        for (const id of TIER_IDS) {
          const t = merged[id]
          next[id] = {
            msg:
              t?.monthly_message_limit != null && t.monthly_message_limit !== ''
                ? String(t.monthly_message_limit)
                : '',
            usd:
              t?.monthly_cost_usd_limit != null && t.monthly_cost_usd_limit !== ''
                ? String(t.monthly_cost_usd_limit)
                : '',
          }
        }
        setTierDrafts(next)
      }
      setCreditSuccess(true)
      setTimeout(() => setCreditSuccess(false), 3000)
    } catch {
      setCreditError('Network error')
    } finally {
      setSavingCredit(false)
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">Loading configurations…</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Login passcode
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Passcodes required to see the login form. One per line. Leave empty to allow anyone to see the login page.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="login-passcodes">Passcodes (one per line)</Label>
              <textarea
                id="login-passcodes"
                rows={6}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={textareaValue}
                onChange={(e) => setTextareaValue(e.target.value)}
                placeholder="passcode1&#10;passcode2"
              />
            </div>
            {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
            {success && <p className="text-sm text-green-600 dark:text-green-400">Saved.</p>}
            <AdminPanelButton type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </AdminPanelButton>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cpu className="h-5 w-5" />
            Chat model
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Default is Claude Sonnet 5 with Anthropic web search. Legacy Sonnet 4.6/4.5 remain available for historical analytics. OpenAI models use web search via the Responses API. Set keys in the server environment:{' '}
            <code className="text-xs">ANTHROPIC_API_KEY</code> or <code className="text-xs">CLAUDE_API_KEY</code> for Claude;{' '}
            <code className="text-xs">OPENAI_API_KEY</code> for GPT-4o;{' '}
            <code className="text-xs">OPENAI_API_KEY_5</code> (or <code className="text-xs">OPENAI_API_KEY</code>) for GPT-5.1.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleSaveLlm} className="flex max-w-md flex-col gap-4">
            <div className="space-y-3">
              <Label id="llm-provider-label">Provider</Label>
              <div
                role="radiogroup"
                aria-labelledby="llm-provider-label"
                className="flex flex-col gap-2 rounded-lg border border-input bg-background p-1"
                style={{ borderColor: 'var(--bd)' }}
              >
                {llmChoices.map((opt) => (
                  <label
                    key={opt.id}
                    className="flex cursor-pointer items-start gap-3 rounded-md px-3 py-2.5 text-sm leading-snug hover:bg-muted/50 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring"
                  >
                    <input
                      type="radio"
                      name="llm-provider"
                      value={opt.id}
                      checked={llmProvider === opt.id}
                      onChange={() => setLlmProvider(opt.id)}
                      className="mt-0.5 size-4 shrink-0 accent-[var(--gold)]"
                    />
                    <span className="min-w-0 flex-1 break-words">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-3">
              <Label>Max output tokens (per model)</Label>
              <p className="text-xs text-muted-foreground">
                Applied when that model is selected. Saved together with the provider choice.
              </p>
              <div className="flex flex-col gap-3 rounded-lg border border-input bg-background p-3">
                {llmChoices.map((opt) => (
                  <div key={`mt-${opt.id}`} className="flex flex-wrap items-center gap-2">
                    <Label htmlFor={`max-tok-${opt.id}`} className="w-48 shrink-0 text-xs font-normal">
                      {opt.label}
                    </Label>
                    <input
                      id={`max-tok-${opt.id}`}
                      type="number"
                      min={1}
                      max={262144}
                      className="h-9 w-32 rounded-md border border-input bg-background px-2 text-sm"
                      value={
                        maxTokensByProvider[opt.id] ??
                        defaultMaxTokensByProvider[opt.id] ??
                        ''
                      }
                      onChange={(e) => {
                        const raw = e.target.value
                        const n = parseInt(raw, 10)
                        const fallback = defaultMaxTokensByProvider[opt.id] ?? 4096
                        if (raw === '' || Number.isNaN(n)) {
                          setMaxTokensByProvider((prev) => ({ ...prev, [opt.id]: fallback }))
                          return
                        }
                        setMaxTokensByProvider((prev) => ({ ...prev, [opt.id]: n }))
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
            {llmError && <p className="text-sm text-red-600 dark:text-red-400">{llmError}</p>}
            {llmSuccess && (
              <p className="text-sm text-green-600 dark:text-green-400">
                {llmSuccessMessage ?? 'Saved.'}
              </p>
            )}
            <AdminPanelButton type="submit" disabled={savingLlm || !llmProvider}>
              {savingLlm ? 'Saving…' : 'Save model'}
            </AdminPanelButton>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5" />
            Credit tiers (monthly, IST)
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Set monthly caps per tier. Assign each user a tier on their profile. Usage is per user against that tier
            only. Empty = unlimited for that cap. Resets at midnight IST on the 1st.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveCredit} className="flex max-w-2xl flex-col gap-6">
            {TIER_IDS.map((id) => (
              <div
                key={id}
                className="grid gap-3 rounded-lg border border-input bg-background p-4 sm:grid-cols-[140px_1fr_1fr]"
              >
                <div className="font-medium text-sm text-foreground sm:pt-2">
                  {tierLabels[id] ?? id.charAt(0).toUpperCase() + id.slice(1)}
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`tier-msg-${id}`}>Max user messages / month</Label>
                  <input
                    id={`tier-msg-${id}`}
                    type="number"
                    min={1}
                    step={1}
                    placeholder="empty = unlimited"
                    className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                    value={tierDrafts[id]?.msg ?? ''}
                    onChange={(e) =>
                      setTierDrafts((prev) => ({
                        ...prev,
                        [id]: { ...prev[id], msg: e.target.value },
                      }))
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`tier-usd-${id}`}>Max spend / month (USD)</Label>
                  <input
                    id={`tier-usd-${id}`}
                    type="number"
                    min={0}
                    step="any"
                    placeholder="empty = unlimited"
                    className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                    value={tierDrafts[id]?.usd ?? ''}
                    onChange={(e) =>
                      setTierDrafts((prev) => ({
                        ...prev,
                        [id]: { ...prev[id], usd: e.target.value },
                      }))
                    }
                  />
                </div>
              </div>
            ))}
            {creditError && <p className="text-sm text-red-600 dark:text-red-400">{creditError}</p>}
            {creditSuccess && <p className="text-sm text-green-600 dark:text-green-400">Saved.</p>}
            <AdminPanelButton type="submit" disabled={savingCredit}>
              {savingCredit ? 'Saving…' : 'Save tier limits'}
            </AdminPanelButton>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
